import {Button, Link, Stack, Typography, useTheme} from '@mui/material';
import {GetPrivacyPolicyLinkParams} from 'domain/usecases';
import {makeGetPrivacyPolicyLink} from 'main/factories/usecases';
import {Modal} from 'presentation/components';
import {LinksNdiSp} from 'presentation/constants/linksNdiEnum';
import {useGetEndpointUrl, useHostName} from 'presentation/hooks';
import {useExternalServiceStore} from 'presentation/pages/private/store/externalSerivceUrlStore';
import {translations} from 'presentation/translations-new';
import React, {useCallback} from 'react';
import {StyledList, StyledListItem} from '../../styles';
import {ExternalLinkEnum} from './constants';
import {ConsentModalItem, ConsentModalProps} from './types';
import {LinksNotredame} from 'presentation/constants/linksNotredameEnum';

const {consentModalItems, consentModalTitle, consentModalDescription} =
	translations['pt-br'].privateFlow.businessAccessPermission.terms;
const {understood, privacyPolicy} = translations['pt-br'].commons;

export const ConsentModal = ({consentModalOpen, setConsentModalOpen}: ConsentModalProps) => {
	const {palette} = useTheme();
	const {getLink} = useGetEndpointUrl(privacyPolicy);
	const cachedURL = useExternalServiceStore(state => state.urls[privacyPolicy]);

	const {
		dataHostNameState: {productCode},
		isNotredameSaude,
	} = useHostName();

	const linksEnum = isNotredameSaude ? LinksNotredame : LinksNdiSp;

	const openLink = useCallback(
		(key: string) => {
			if (!key.includes(ExternalLinkEnum.PORTAL)) {
				window.open(linksEnum.LGPD);
				return;
			}
			if (cachedURL) {
				window.open(cachedURL);
				return;
			}

			const cdEmpresaPlano = productCode?.[0] || 0;
			const params: GetPrivacyPolicyLinkParams = {cdEmpresaPlano, isPpo: isNotredameSaude};
			getLink(makeGetPrivacyPolicyLink, false, false, params);
		},
		[cachedURL],
	);

	return (
		<Modal.Root open={consentModalOpen} onClose={() => setConsentModalOpen(false)}>
			<Modal.Header title={consentModalTitle} onClose={() => setConsentModalOpen(false)} />
			<Modal.Content sx={{padding: '16px'}}>
				<Stack spacing={2}>
					<Typography variant="body2" color="text.primary">
						{consentModalDescription}
					</Typography>

					{consentModalItems.map((item: ConsentModalItem, index: number) => (
						<Stack key={index}>
							<Typography variant="body2" fontWeight={600} color="text.primary">
								{item.title}
							</Typography>
							{item.paragraph ? (
								<Typography variant="body2" color="text.primary">
									{item.paragraph}
								</Typography>
							) : null}
							{item.bullets ? (
								<StyledList>
									{item.bullets.map((bullet: string, bulletIndex: number) => (
										<StyledListItem
											key={bulletIndex}
											sx={{fontSize: '0.875rem', fontWeight: 400}}>
											{bullet}
										</StyledListItem>
									))}
								</StyledList>
							) : null}
							{item.info ? (
								<Typography variant="body2" color="text.primary">
									{item.info}
								</Typography>
							) : null}

							{item?.paragraphs?.map((paragraph, paragraphIndex) => {
								if (typeof paragraph === 'string') {
									return (
										<Typography
											key={paragraphIndex}
											variant="body2"
											color="text.primary">
											{paragraph}
										</Typography>
									);
								}

								return (
									<Typography
										key={paragraphIndex}
										variant="body2"
										color="text.primary">
										{paragraph.partOne}

										<Link
											component={'button'}
											sx={{
												color: palette.primary.main,
												verticalAlign: 'initial',
												textAlign: 'start',
											}}
											onClick={() => openLink(paragraph.privacyPortal)}>
											{paragraph.privacyPortal}
										</Link>

										{paragraph.partTwo}
									</Typography>
								);
							})}
						</Stack>
					))}
				</Stack>
			</Modal.Content>

			<Modal.Footer>
				<Button
					fullWidth
					variant="contained"
					size="large"
					color="primary"
					onClick={() => setConsentModalOpen(false)}>
					{understood}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
};
